import { Component } from '@theme/component';
import { morph, recreateAppBlockScripts } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

// --- HC quick add analytics -------------------------------------------------
// The card's Add form and the quick-add window carry a hidden line property,
// _hc_quick_add (snippets/quick-add.liquid, blocks/buy-buttons.liquid), saved
// on the order's line item. It says which layout the shopper used (the owner,
// 2026-10-12: "quick add mobile vs quick add desktop"): mobile below the
// theme's phone breakpoint (750px, the same switch as the mobile quick add
// setting), desktop above it - so most tablets count as desktop. Set on
// window in the capture phase: Horizon's declarative on:submit runs from a
// capture listener on document (component.js) and reads the form there, so
// window is the only earlier stop.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    const field = form?.querySelector('input[name="properties[_hc_quick_add]"]');
    if (field instanceof HTMLInputElement) {
      field.value = isMobileBreakpoint() ? 'Quick add - mobile' : 'Quick add - desktop';
    }
  },
  true
);

// --- Quick-add section-id discovery ------------------------------------------
// Section-rendering the quick-add template's main section
// (?section_id=<id>&view=quick-add) returns the same [data-product-grid-content]
// subtree at ~26KB/~300ms cold versus ~130KB wire and a multi-second cold
// render for the full page, because Shopify skips the layout and header/footer
// groups. The fully-qualified id (template--<id>__main) is unknowable in
// Liquid outside that template, so it is discovered from the first full-page
// response and persisted per theme. Re-creating the template changes the id;
// the fetch path self-heals by falling back to the full page and
// re-discovering.
const QA_SID_KEY = () => `hc-qa-section-id:${window.Shopify?.theme?.id ?? 'unknown'}`;
let qaSectionId = null;
let qaDiscoveryScheduled = false;

function readQaSectionId() {
  if (qaSectionId) return qaSectionId;
  try {
    qaSectionId = localStorage.getItem(QA_SID_KEY());
  } catch {
    // localStorage unavailable (private mode/quota) - fall back to per-page memory
  }
  return qaSectionId;
}

/** @param {string | null} id */
function writeQaSectionId(id) {
  qaSectionId = id;
  try {
    if (id) {
      localStorage.setItem(QA_SID_KEY(), id);
    } else {
      localStorage.removeItem(QA_SID_KEY());
    }
  } catch {
    // best effort only
  }
}

/** @param {Document} doc */
function extractQaSectionId(doc) {
  // The anchor's own section wrapper, not any [data-section-id] in the page -
  // other sections carry their own ids and default-variant products render no
  // picker to read it from.
  const wrapper = doc.querySelector('[data-product-grid-content]')?.closest('.shopify-section');
  if (!wrapper?.id?.startsWith('shopify-section-')) return null;
  const id = wrapper.id.slice('shopify-section-'.length);
  return /^template--\d+__/.test(id) ? id : null;
}

/**
 * One tiny idle fetch per browser discovers the section id before any dialog
 * opens, so even the session's very first open takes the fast path.
 * @param {string} [candidateUrl]
 */
function scheduleQaSectionDiscovery(candidateUrl) {
  if (readQaSectionId() || qaDiscoveryScheduled) return;
  qaDiscoveryScheduled = true;
  const idle = window.requestIdleCallback ?? ((/** @type {() => void} */ cb) => setTimeout(cb, 3000));
  idle(
    async () => {
      try {
        const href =
          candidateUrl || document.querySelector('product-card a[href*="/products/"]')?.getAttribute('href');
        if (!href) {
          qaDiscoveryScheduled = false;
          return;
        }
        const url = new URL(href, location.origin);
        url.searchParams.set('view', 'quick-add');
        const res = await fetch(url, { priority: 'low' });
        if (!res.ok) return;
        const id = extractQaSectionId(new DOMParser().parseFromString(await res.text(), 'text/html'));
        if (id) writeQaSectionId(id);
      } catch {
        // opportunistic - the first click self-discovers via its full-page fallback
      }
    },
    { timeout: 8000 }
  );
}

// --- Viewport prefetch for touch devices -------------------------------------
// Hover prefetch never fires on touch (pointerenter arrives inside the tap
// itself), so the first few visible cards warm at idle instead. Desktop keeps
// hover/focus prefetch only - its request volume is unchanged. Only the cheap
// section-render path is allowed here, never a cold full-page fetch.
const QA_VIEWPORT_PREFETCH_MAX = 6;
let qaViewportPrefetched = 0;
/** @type {IntersectionObserver | null} */
let qaPrefetchObserver = null;

/** @param {QuickAddComponent} component */
function observeForViewportPrefetch(component) {
  if (window.matchMedia('(hover: hover)').matches) return;
  const connection = /** @type {{ saveData?: boolean, effectiveType?: string } | undefined} */ (
    navigator.connection
  );
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;

  qaPrefetchObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        qaPrefetchObserver?.unobserve(entry.target);
        if (qaViewportPrefetched >= QA_VIEWPORT_PREFETCH_MAX) continue;
        if (!readQaSectionId()) continue;
        qaViewportPrefetched += 1;
        const idle = window.requestIdleCallback ?? ((/** @type {() => void} */ cb) => setTimeout(cb, 200));
        idle(() => /** @type {QuickAddComponent} */ (entry.target).prefetch?.());
      }
    },
    { threshold: 0.5 }
  );
  qaPrefetchObserver.observe(component);
}

// --- Skeleton lifecycle ------------------------------------------------------
// HC: the loading ring is a CSS-animated pseudo-element inside an open
// <dialog> (top layer). iOS Safari gives that animation its own compositor
// layer, and when the ring is created and destroyed within the same frame the
// dialog is animating open - every warm open: the cached fetch refills the
// content in a microtask - WebKit occasionally never tears the layer down. The
// shopper then sees fully loaded content with a ring spinning (or frozen) over
// it until the dialog closes and compositing is rebuilt. The DOM is correct the
// whole time, which is why every earlier fix (display:none skeleton, stale
// children cleared, transform-based spin, 12s abort) left this one standing.
// So the ring's lifetime is made deterministic: it appears only after
// SKELETON_DELAY_MS (the dialog's open animation is 125ms, so never during it;
// warm opens never show it at all), stays at least SKELETON_MIN_MS once shown,
// and is torn down explicitly - animations cancelled first, attribute second.
const SKELETON_DELAY_MS = 150;
const SKELETON_MIN_MS = 400;
let qaOpenSeq = 0;

/**
 * HC: on desktop the dialog's media column is a scroll column showing one
 * colour's whole run, and lazy loading left every slide past the browser's
 * preload margin blank until scrolled to (the fourth and fifth of a
 * five-image run, measured live). Once the markup is in the document, flip
 * the run eager at low priority - behind the first image, which is already
 * high. The fetched HTML was inert until this moment, so nothing competed
 * with the page. Phones show a single thumbnail (the other slides are
 * display:none), so there the images stay lazy and never download. Runs
 * after the sheet fills and again after a colour change replaces the
 * gallery. The images' sizes attr is explicit in the quick-add view, so no
 * pinning is needed when lazy comes off.
 * @param {ParentNode | null | undefined} root
 */
function loadGalleryRun(root) {
  if (!root || isMobileBreakpoint()) return;
  for (const img of root.querySelectorAll('.product-information__media .product-media img[loading="lazy"]')) {
    img.setAttribute('fetchpriority', 'low');
    img.removeAttribute('loading');
  }
}

export class QuickAddComponent extends Component {
  /**
   * Per-fetch controllers: parallel fetches for different products must not
   * abort each other (the promise cache already dedupes same-URL calls).
   * Aborts happen only on disconnect.
   * @type {Set<AbortController>}
   */
  #activeFetchControllers = new Set();
  /** @type {Map<string, Promise<Element | null>>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID. The URL is checked first because
   * ProductCard.getSelectedVariantId returns null once the card link carries
   * ?variant= - which is every open after a swatch click.
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const url = this.productPageUrl;
    if (url) {
      const variantParam = new URL(url, location.origin).searchParams.get('variant');
      if (variantParam) return variantParam;
    }
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Fresh controller per connect: a reused aborted controller would leave
    // the cart listener permanently dead after any editor/facet morph.
    this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.addEventListener('pointerenter', this.#prefetch, { once: true });
    this.addEventListener('focusin', this.#prefetch, { once: true });
    scheduleQaSectionDiscovery(this.productPageUrl);
    observeForViewportPrefetch(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    for (const controller of this.#activeFetchControllers) controller.abort();
    this.#activeFetchControllers.clear();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    qaPrefetchObserver?.unobserve(this);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Invalidates cached content when the cart changes. Only this product's
   * cache entries actually go stale (quantity-rule caps embedded in the
   * HTML), so other products keep their warmed entries when the event
   * identifies the product; the hover prefetch re-arms either way.
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    const clearAndRearm = () => {
      this.#cachedContent.clear();
      // addEventListener dedupes on (type, callback, capture) - safe to re-add.
      this.addEventListener('pointerenter', this.#prefetch, { once: true });
      this.addEventListener('focusin', this.#prefetch, { once: true });
    };
    if (!event.promise) {
      clearAndRearm();
      return;
    }
    event.promise
      .then(({ detail }) => {
        const changedId = detail?.productId != null ? String(detail.productId) : null;
        const myId = this.dataset.productId;
        // cart-items/quick-order-list events carry no productId -> conservative clear.
        if (changedId && myId && changedId !== myId) return;
        clearAndRearm();
      })
      .catch(() => {});
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const modalContent = document.getElementById('quick-add-modal-content');

    // Open first: a cold fetch shows the skeleton instead of a frozen page.
    // Warm opens never flash it - the cached promise's continuation (clone +
    // morph) runs in a microtask before the rAF-deferred showModal paints.
    /* Clear stale content from the previous open BEFORE the skeleton goes
       up. iOS has painted those stale children straight through the hidden
       skeleton (content + frozen spinner, the owner's screenshots), so the
       ghost must not exist at all. Warm opens do not flash: the cached
       promise refills the element in a microtask, before the next paint. */
    modalContent?.replaceChildren();
    // Pending = empty sheet at the skeleton's height, no ring yet; the ring
    // itself is armed on a timer (see the skeleton lifecycle note above).
    modalContent?.setAttribute('data-quick-add-pending', '');
    modalContent?.setAttribute('aria-busy', 'true');
    this.toggleAttribute('data-loading', true);
    const skeleton = this.#armSkeleton(modalContent);
    this.#openQuickAddModal();
    // HC: hc-qa-offer.js hides any previous product's offer banner now and
    // fills it again on the loaded event below.
    document.dispatchEvent(new CustomEvent('hc:quick-add:opening'));

    try {
      const productGrid = await this.#loadProductGrid(currentUrl);
      if (!productGrid) throw new Error('quick-add: no product grid in response');

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      // A ring that is up stays up for its minimum time, so its creation and
      // removal never land in adjacent frames.
      await skeleton.settle();
      await this.updateQuickAddModal(freshContent);
      // HC: the template's Full Product Details link is the bare product url;
      // point it at the variant this dialog opened on (the swatch picked on
      // the card), same as the title link. Later picks are synced in
      // #handleProductSelect.
      // The dialog is a separate element (quick-add-dialog), not inside this card.
      const hcDialog = document.getElementById('quick-add-modal-content')?.closest('quick-add-dialog, dialog');
      const hcOpenedOn = /** @type {HTMLAnchorElement | null} */ (
        hcDialog?.querySelector('.view-product-title a[href*="variant="], .product-header a[href*="variant="]') ?? null
      );
      if (hcDialog && hcOpenedOn) {
        hcDialog.querySelectorAll('a.button-unstyled[href*="/products/"]').forEach((link) => {
          /** @type {HTMLAnchorElement} */ (link).href = hcOpenedOn.href;
        });
      }
      document.dispatchEvent(new CustomEvent('hc:quick-add:loaded', { detail: { url: currentUrl } }));
    } catch {
      // Never strand an open dialog on the skeleton - fall back to the PDP,
      // matching the selling-plans path above.
      this.#closeQuickAddModal();
      if (currentUrl) window.location.href = currentUrl;
      return;
    } finally {
      skeleton.clear();
      const liveContent = document.getElementById('quick-add-modal-content') || modalContent;
      liveContent?.setAttribute('aria-busy', 'false');
      this.toggleAttribute('data-loading', false);
    }
  };

  /**
   * Arms the loading ring for one open (skeleton lifecycle note above).
   * @param {HTMLElement | null} modalContent
   * @returns {{ settle: () => Promise<void>, clear: () => void }}
   */
  #armSkeleton(modalContent) {
    const seq = ++qaOpenSeq;
    let shownAt = 0;
    const timer = window.setTimeout(() => {
      // A newer open owns the sheet now, or the shopper already closed it.
      if (seq !== qaOpenSeq) return;
      const dialog = document.getElementById('quick-add-dialog')?.querySelector('dialog');
      if (dialog && !dialog.open) return;
      const live = document.getElementById('quick-add-modal-content') || modalContent;
      if (!live?.hasAttribute('data-quick-add-pending')) return;
      live.setAttribute('data-quick-add-loading', '');
      shownAt = performance.now();
    }, SKELETON_DELAY_MS);

    return {
      settle: () => {
        if (!shownAt) return Promise.resolve();
        const remaining = SKELETON_MIN_MS - (performance.now() - shownAt);
        return remaining > 0 ? new Promise((resolve) => setTimeout(resolve, remaining)) : Promise.resolve();
      },
      clear: () => {
        clearTimeout(timer);
        /* Re-query by id so a rebuilt node is the one cleared; the captured
           reference is the fallback. Cancel the ring's animation BEFORE the
           attribute goes - the compositor layer is released by the cancel,
           not by the pseudo-element vanishing under it. */
        const live = document.getElementById('quick-add-modal-content') || modalContent;
        if (!live) return;
        for (const animation of live.getAnimations?.({ subtree: true }) ?? []) animation.cancel();
        live.removeAttribute('data-quick-add-loading');
        live.removeAttribute('data-quick-add-pending');
      },
    };
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    // The mobile rebuild removes .product-details; the modal content element
    // is the actual scroll container there (harmless no-op on desktop).
    const modalContent = dialogComponent.querySelector('.quick-add-modal__content');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Returns the product grid for the URL, fetching and caching it if needed.
   * The cache stores in-flight promises so hover, swatch-change, viewport,
   * and click prefetches share one request per URL instead of racing.
   * @param {string} currentUrl - The product page URL
   * @returns {Promise<Element | null>}
   */
  #loadProductGrid(currentUrl) {
    if (!currentUrl) return Promise.resolve(null);

    let gridPromise = this.#cachedContent.get(currentUrl);

    if (!gridPromise) {
      gridPromise = this.fetchProductPage(currentUrl)
        .then((html) => {
          const gridElement = html?.querySelector('[data-product-grid-content]');
          if (!gridElement) {
            // Aborted or unexpected markup - drop the entry so the next open retries.
            this.#cachedContent.delete(currentUrl);
            return null;
          }
          const grid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#warmImages(grid);
          return grid;
        })
        .catch((error) => {
          this.#cachedContent.delete(currentUrl);
          throw error;
        });
      this.#cachedContent.set(currentUrl, gridPromise);
    }

    return gridPromise;
  }

  /**
   * Starts downloading the first gallery images as soon as the HTML arrives -
   * DOMParser documents never fetch images, so without this the first pixel
   * couldn't start before the dialog opens. Copying the imgs' own attributes
   * (sizes before srcset) makes the warm request select the exact candidate
   * the dialog will render. Two is deliberate: this also runs for hover and
   * viewport PREFETCHES, which must stay cheap. On desktop the rest of the
   * colour's run is flipped eager by loadGalleryRun once the markup enters
   * the dialog.
   * @param {Element} grid
   */
  #warmImages(grid) {
    const imgs = grid.querySelectorAll('.product-information__media .product-media img');
    for (const img of [...imgs].slice(0, 2)) {
      const warm = new Image();
      warm.sizes = img.getAttribute('sizes') || '';
      warm.srcset = img.getAttribute('srcset') || '';
      warm.src = img.getAttribute('src') || '';
    }
  }

  /**
   * Warms the dialog content cache for a URL (default: the card's current
   * one). Public so product-card's hover and swatch-change handlers route
   * through the same promise cache as clicks.
   * @param {string} [url]
   */
  prefetch(url = this.productPageUrl) {
    if (this.dataset.usesSellingPlans === 'true') return;
    this.#loadProductGrid(url).catch(() => {});
  }

  #prefetch = () => this.prefetch();

  /**
   * Fetches the quick-add content for a product.
   *
   * Uses the Section Rendering API when the quick-add template's main section
   * id is known - Shopify then skips the layout and header/footer groups,
   * which is the difference between ~300ms and multi-second cold renders.
   * Falls back to the full ?view=quick-add page (which also re-discovers the
   * id) whenever the id is unknown or stale, so a re-created template
   * self-heals instead of emptying the dialog.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const controller = new AbortController();
    this.#activeFetchControllers.add(controller);
    /* Mobile networks hang requests without failing them; with no timeout
       the dialog's finally never ran and the loading spinner sat there
       forever ("sometimes opens with this loading thing"). Abort after 12s
       so the existing catch path - close the dialog, go to the product
       page - takes over instead. */
    const hangGuard = setTimeout(() => controller.abort(), 12000);

    /** @param {string | null} sectionId */
    const buildUrl = (sectionId) => {
      // ?variant= stays in the URL on purpose: it scopes the gallery to the
      // selected colour and pre-checks the radio, so #syncVariantSelection
      // skips its follow-up section fetch. Section renders are cheap enough
      // that per-variant cache entries beat a shared-but-wrong one.
      const url = new URL(productPageUrl, window.location.origin);
      url.searchParams.delete('section_id');
      url.searchParams.set('view', 'quick-add');
      if (sectionId) url.searchParams.set('section_id', sectionId);
      return url;
    };

    try {
      const sectionId = readQaSectionId();

      if (sectionId) {
        const response = await fetch(buildUrl(sectionId), { signal: controller.signal });
        if (response.ok) {
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (html.querySelector('[data-product-grid-content]')) return html;
        }
        // 404 or unexpected shape: the stored id is stale (template
        // re-created) - drop it and self-heal via the full page below.
        writeQaSectionId(null);
      }

      const response = await fetch(buildUrl(null), { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const discovered = extractQaSectionId(html);
      if (discovered) writeQaSectionId(discovered);
      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      clearTimeout(hangGuard);
      this.#activeFetchControllers.delete(controller);
    }
  }

  /**
   * Places the product grid content into the modal.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      // The rebuild below discards .product-details wholesale; the rating
      // badge, vendor, short description, and Full details link ride along,
      // mirroring the desktop order.
      const ratingBadge = productGrid.querySelector('[class*="jdgm"]');
      const fullDetailsLink = productGrid.querySelector('a.button-unstyled');
      const vendorText = productGrid.querySelector('[class*="__text_U4jNn9"]');
      const shortDescription = productGrid.querySelector('[class*="__text_K3adRc"]');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      // The rebuild removes the template's h1 - keep a heading in the tree
      // (styles neutralized in quick-add-modal-styles so nothing shifts).
      const titleHeading = document.createElement('h2');
      titleHeading.className = 'product-header__title';
      titleHeading.appendChild(productTitle);

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(titleHeading);
      if (vendorText) {
        productHeader.appendChild(vendorText);
      }
      if (ratingBadge) {
        productHeader.appendChild(ratingBadge);
      }
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (fullDetailsLink) {
        productGrid.appendChild(fullDetailsLink);
      }
      if (shortDescription) {
        productGrid.appendChild(shortDescription);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    // Same product again (reopen/variant change): morph preserves live state.
    // Different product: adopt the fresh clone's children directly - a
    // full-tree morph diff against another product's DOM costs main-thread
    // time for nothing.
    const incomingUrl = this.productPageUrl;
    const isSameProduct = modalContent.dataset.quickAddProductUrl === incomingUrl && modalContent.childElementCount > 0;
    if (isSameProduct) {
      morph(modalContent, productGrid);
    } else {
      modalContent.replaceChildren(...productGrid.childNodes);
      // App-block scripts (the Judge.me badge) never execute after a clone
      // unless recreated - morph does this internally; adoption must too.
      recreateAppBlockScripts(modalContent);
    }
    modalContent.dataset.quickAddProductUrl = incomingUrl;
    loadGalleryRun(modalContent);

    // Name the dialog for screen readers - on mobile the rebuild is the only
    // title source, and on first announcement the static label alone reads
    // as an anonymous "dialog".
    const dialog = modalContent.closest('dialog');
    const title = this.dataset.productTitle;
    if (dialog && title) dialog.setAttribute('aria-label', `Quick add: ${title}`);

    // The desktop gallery column scrolls with a hidden scrollbar and
    // contains nothing focusable - give keyboard users a scrollable region.
    const mediaColumn = modalContent.querySelector('.product-information__media');
    if (mediaColumn) {
      mediaColumn.setAttribute('tabindex', '0');
      mediaColumn.setAttribute('role', 'region');
      mediaColumn.setAttribute('aria-label', 'Product images');
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Fresh controller per connect - a reused aborted one would leave these
    // listeners permanently dead after any disconnect/reconnect cycle.
    this.#abortController = new AbortController();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        // The media gallery replaces itself from this same response (its own
        // listener, attached first, has already run) - the new run arrives
        // lazy, so give it the desktop flip too.
        setTimeout(() => loadGalleryRun(this), 0);
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
        // HC: the "Full Product Details" link-style button carries the same
        // chosen variant, so the PDP opens on the colour/size picked here.
        this.querySelectorAll('a.button-unstyled[href*="/products/"]').forEach((link) => {
          /** @type {HTMLAnchorElement} */ (link).href = anchorElement.href;
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
